// src/components/admin/DialogGuiaDetalle.js
//
// Vista de detalle del Guía: datos, zona, modelo económico, lista de
// afiliados con contrataciones, y estadísticas.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Stack, Box, Typography, Chip, Divider, Grid, Table, TableHead,
  TableBody, TableRow, TableCell, Avatar, LinearProgress, Alert, Paper,
} from '@mui/material';
import PhoneIcon        from '@mui/icons-material/Phone';
import EmailIcon        from '@mui/icons-material/Email';
import PlaceIcon        from '@mui/icons-material/Place';
import DescriptionIcon  from '@mui/icons-material/Description';

import { guias as apiGuias } from '../../api/guiasApi';
import { useApp } from '../../context/AppContext';
import { fechaLocal } from '../../utils/format';

const chipContrata = (v) =>
  v ? <Chip label="Sí" size="small" color="primary" /> : <Chip label="No" size="small" variant="outlined" />;

export default function DialogGuiaDetalle({ abierto, guiaId, onCerrar }) {
  const { notificar } = useApp();
  const [cargando, setCargando] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!abierto || !guiaId) return;
    setCargando(true);
    apiGuias.obtener(guiaId)
      .then(r => setData(r))
      .catch(e => notificar(e.mensajeUsuario || 'No se pudo cargar el detalle.', 'error'))
      .finally(() => setCargando(false));
  }, [abierto, guiaId, notificar]);

  const g = data?.guia;
  const afiliados = data?.afiliados || [];
  const est = data?.estadisticas || {};

  return (
    <Dialog open={abierto} onClose={onCerrar} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar src={g?.fotoUrl} sx={{ bgcolor: 'primary.light' }}>
            {g?.nombreCompleto?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h6">{g?.nombreCompleto || 'Cargando…'}</Typography>
            {g && (
              <Typography variant="caption" color="text.secondary">
                {g.tipoDocumento} {g.numeroDocumento} · Guía desde {fechaLocal(g.fechaAlta)}
              </Typography>
            )}
          </Box>
          {g && <Chip label={g.estado} size="small" color={g.estado === 'ACTIVO' ? 'success' : g.estado === 'PAUSADO' ? 'warning' : 'default'} />}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {cargando ? <LinearProgress /> : g && (
          <>
            {/* Estadísticas */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Afiliados totales', valor: est.totalAfiliados, color: 'default' },
                { label: 'Activos',           valor: est.activos,        color: 'primary' },
                { label: 'Con Negocio',       valor: est.conHolaVeci,    color: 'primary' },
                { label: 'Con Local',         valor: est.conPOS,         color: 'secondary' },
                { label: 'Con ambos (360°)',  valor: est.conAmbos,       color: 'warning' },
              ].map((s, i) => (
                <Grid item xs={6} md={2.4} key={i}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color={`${s.color}.main`} fontWeight={700}>
                      {s.valor ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Contacto + zona */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="overline" color="text.secondary">Contacto</Typography>
                <Divider sx={{ mb: 1 }} />
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1}><PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{g.telefono}</Typography></Stack>
                  {g.correo && <Stack direction="row" spacing={1}><EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">{g.correo}</Typography></Stack>}
                  {g.rut && <Typography variant="caption" color="text.secondary">RUT: {g.rut}</Typography>}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="overline" color="text.secondary">Zona</Typography>
                <Divider sx={{ mb: 1 }} />
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1}><PlaceIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {g.municipio || '—'} · Radio {g.radioOperacionKm} km
                    </Typography></Stack>
                  {g.direccionBase && <Typography variant="body2">{g.direccionBase}</Typography>}
                  {g.latitud && g.longitud &&
                    <Typography variant="caption" color="text.secondary">
                      {g.latitud}, {g.longitud}
                    </Typography>}
                  {g.barriosCubiertos &&
                    <Typography variant="caption" color="text.secondary">
                      Barrios: {g.barriosCubiertos}
                    </Typography>}
                </Stack>
              </Grid>
            </Grid>

            {/* Modelo económico */}
            <Typography variant="overline" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
              Modelo económico
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={4}>
              <Box><Typography variant="caption" color="text.secondary">Comisión</Typography>
                <Typography variant="h6">{g.porcentajeComision}%</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Bono al día {g.diasParaBono}</Typography>
                <Typography variant="h6">${g.bonoAfiliacionInicial?.toLocaleString('es-CO')}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Duración recurrente</Typography>
                <Typography variant="h6">
                  {g.mesesRecurrente > 0 ? `${g.mesesRecurrente} meses` : 'Sin fin'}
                </Typography></Box>
            </Stack>
            {g.contratoUrl && (
              <Button size="small" startIcon={<DescriptionIcon />} href={g.contratoUrl}
                      target="_blank" rel="noopener noreferrer" sx={{ mt: 1 }}>
                Ver contrato
              </Button>
            )}

            {/* Afiliados */}
            <Typography variant="overline" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
              Afiliados ({afiliados.length})
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {afiliados.length === 0 ? (
              <Alert severity="info">Este Guía aún no tiene negocios afiliados.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Empresa</TableCell>
                    <TableCell>Fecha afiliación</TableCell>
                    <TableCell align="center">Negocio</TableCell>
                    <TableCell align="center">Local</TableCell>
                    <TableCell align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {afiliados.map(a => (
                    <TableRow key={a.empresaId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {a.nombreComercial || a.razonSocial}
                        </Typography>
                        {a.nombreComercial && a.nombreComercial !== a.razonSocial && (
                          <Typography variant="caption" color="text.secondary">
                            {a.razonSocial}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {a.fechaAfiliacionGuia
                          ? fechaLocal(a.fechaAfiliacionGuia)
                          : '—'}
                      </TableCell>
                      <TableCell align="center">{chipContrata(a.contrataHolaVeci)}</TableCell>
                      <TableCell align="center">{chipContrata(a.contrataPOS)}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={a.activo ? 'Activo' : 'Inactivo'}
                              color={a.activo ? 'success' : 'default'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
